import { defineStore } from 'pinia';
import { authApi } from '@/services/api/auth.api';
import { secureStorage } from '@/services/storage/secureStorage.service';
import { STORAGE_KEYS } from '@/constants/routes.const';
import type { User } from '@/models/User';
import type { ApiError, LoginPayload } from '@/types/api.types';
import router from '@/router';
import { ROUTE_NAMES } from '@/constants/routes.const';
import { formatAuthErrorMessage } from '@/utils/auth-error.util';

interface AuthState {
  user: User | null;
  isGuest: boolean;
  guestExpiresAt: number | null;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isGuest: false,
    guestExpiresAt: null,
    isLoading: false,
    error: null,
    isInitialized: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    hasActiveGuestSession: (state) =>
      state.isGuest && state.guestExpiresAt !== null && state.guestExpiresAt > Date.now(),
    isMember: (state) => state.user?.role === 'member' || state.user?.role === 'admin',
    isAdmin: (state) => state.user?.role === 'admin',
  },

  actions: {
    async clearLocalSession() {
      await Promise.all([
        secureStorage.remove(STORAGE_KEYS.ACCESS_TOKEN),
        secureStorage.remove(STORAGE_KEYS.REFRESH_TOKEN),
        secureStorage.remove(STORAGE_KEYS.USER),
        secureStorage.remove(STORAGE_KEYS.LAST_ACTIVITY),
      ]);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('guest_expires_at');
      }
      this.user = null;
      this.isGuest = false;
      this.guestExpiresAt = null;
    },

    async recordActivity() {
      const now = Date.now();
      if (this.user) {
        await secureStorage.set(STORAGE_KEYS.LAST_ACTIVITY, String(now));
      } else if (this.isGuest) {
        this.guestExpiresAt = now + 5 * 60 * 1000;
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('guest_expires_at', String(this.guestExpiresAt));
        }
      }
    },

    startGuest() {
      this.user = null;
      this.isGuest = true;
      this.guestExpiresAt = Date.now() + 5 * 60 * 1000;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('guest_expires_at', String(this.guestExpiresAt));
      }
    },

    expireGuest() {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('guest_expires_at');
      }
      this.isGuest = false;
      this.guestExpiresAt = null;
    },

    async validateInactivity() {
      if (this.isGuest) {
        if (!this.guestExpiresAt || this.guestExpiresAt <= Date.now()) {
          this.expireGuest();
          return false;
        }
        return true;
      }

      if (this.user) {
        const lastActivity = await secureStorage.get(STORAGE_KEYS.LAST_ACTIVITY);
        if (!lastActivity || Date.now() - Number(lastActivity) > 30 * 60 * 1000) {
          await this.clearLocalSession();
          return false;
        }
      }
      return true;
    },

    /**
     * Called once on app boot. Restores a session from secure storage
     * without requiring the user to log in again ("auto login").
     */
    async restoreSession() {
      this.isLoading = true;
      try {
        const storedGuestExpiry =
          typeof sessionStorage !== 'undefined'
            ? Number(sessionStorage.getItem('guest_expires_at'))
            : 0;
        const [token, refreshToken, lastActivity] = await Promise.all([
          secureStorage.get(STORAGE_KEYS.ACCESS_TOKEN),
          secureStorage.get(STORAGE_KEYS.REFRESH_TOKEN),
          secureStorage.get(STORAGE_KEYS.LAST_ACTIVITY),
        ]);
        const inactiveTooLong = !lastActivity || Date.now() - Number(lastActivity) > 30 * 60 * 1000;

        if (token && refreshToken && !inactiveTooLong) {
          const validatedUser = (await authApi.me()) as User;
          this.user = validatedUser;
          await secureStorage.set(STORAGE_KEYS.USER, JSON.stringify(validatedUser));
          await this.recordActivity();
        } else {
          await this.clearLocalSession();
          if (storedGuestExpiry > Date.now()) {
            this.isGuest = true;
            this.guestExpiresAt = storedGuestExpiry;
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.setItem('guest_expires_at', String(storedGuestExpiry));
            }
          }
        }
      } catch {
        await this.clearLocalSession();
      } finally {
        this.isLoading = false;
        this.isInitialized = true;
      }
    },

    async login(payload: LoginPayload) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await authApi.login(payload);
        await secureStorage.set(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
        await secureStorage.set(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
        await secureStorage.set(STORAGE_KEYS.USER, JSON.stringify(response.user));
        await secureStorage.set(STORAGE_KEYS.LAST_ACTIVITY, String(Date.now()));
        this.expireGuest();
        this.user = response.user as unknown as User;
        return true;
      } catch (err) {
        const apiError = err as ApiError;
        this.error = formatAuthErrorMessage(apiError, 'login');
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async register(payload: Parameters<typeof authApi.register>[0]) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await authApi.register(payload);
        await secureStorage.set(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
        await secureStorage.set(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
        await secureStorage.set(STORAGE_KEYS.USER, JSON.stringify(response.user));
        await secureStorage.set(STORAGE_KEYS.LAST_ACTIVITY, String(Date.now()));
        this.expireGuest();
        this.user = response.user as User;
        return true;
      } catch (err) {
        this.error = formatAuthErrorMessage(err as ApiError, 'register');
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async logout() {
      try {
        await authApi.logout();
      } catch {
        // best-effort; still clear local session
      }
      await this.clearLocalSession();
      router.replace({ name: ROUTE_NAMES.LOGIN });
    },

    async deleteAccount() {
      this.isLoading = true;
      this.error = null;
      try {
        await authApi.deleteAccount();
        await this.clearLocalSession();
        await router.replace({ name: ROUTE_NAMES.LOGIN, query: { deleted: '1' } });
        return true;
      } catch (err) {
        this.error = (err as ApiError).message ?? 'We could not delete your account.';
        return false;
      } finally {
        this.isLoading = false;
      }
    },
  },
});

/**
 * Sets up the global auth:expired event listener.
 * This should be called once during application initialization (e.g., in main.ts).
 * When the axios interceptor fails to refresh the token, it dispatches this event
 * so the store can clear state and redirect to login.
 */
export function setupAuthExpiredListener(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('auth:expired', () => {
    const store = useAuthStore();
    void store.clearLocalSession().finally(() => {
      void router.replace({ name: ROUTE_NAMES.LOGIN, query: { expired: '1' } });
    });
  });
}
